// src/features/files/hooks/useFiles.js
import { useCallback } from 'react';
import { filesAPI } from '../services/files';
import { useApi } from '../../../hooks/api/useApi';
import i18n from '../../../i18n';

// Same as the server's MAX_UPLOAD_FILE_SIZE. Checked here too because when the
// server refuses a body mid-upload, browsers often report a network error
// instead of its 413, so the user would never see why.
export const MAX_UPLOAD_BYTES = 500 * 1024 * 1024;

const tooLarge = () => ({
  success: false,
  error: i18n.t('errors:REQUEST__TOO_LARGE'),
  errorCode: 'REQUEST__TOO_LARGE',
});

export function useFiles() {
  const { loading, error, execute, clearError } = useApi();

  // Upload file (streamed; options.onUploadProgress gets axios progress events)
  const uploadFile = useCallback(async (file, options = {}) => {
    if (file.size > MAX_UPLOAD_BYTES) return tooLarge();
    const params = new URLSearchParams();
    
    // Add folder_id if specified
    if (options.folder_id !== undefined && options.folder_id !== null) {
      params.append('folder_id', options.folder_id);
    }
    
    if (options.is_system_file !== undefined) {
      params.append('is_system_file', options.is_system_file);
    }
    
    if (options.is_protected !== undefined) {
      params.append('is_protected', options.is_protected);
    }
    
    if (options.retention_days !== undefined) {
      params.append('retention_days', options.retention_days);
    }

    const result = await execute(() => filesAPI.uploadFile(file, params.toString(), {
      onUploadProgress: options.onUploadProgress,
    }));
    return result;
  }, [execute]);

  // Download file
  const downloadFile = useCallback(async (fileId, previewSize = 'original', versionNumber = null) => {
    const result = await execute(() => filesAPI.downloadFile(fileId, previewSize, versionNumber));
    return result;
  }, [execute]);

  // Get file details
  const getFileDetails = useCallback(async (fileId) => {
    const result = await execute(() => filesAPI.getFileDetails(fileId));
    return result;
  }, [execute]);

  // List my files
  const listMyFiles = useCallback(async (limit = null, offset = null) => {
    const result = await execute(() => filesAPI.listMyFiles(limit, offset));
    return result;
  }, [execute]);

  // List department files
  const listDepartmentFiles = useCallback(async (limit = null, offset = null) => {
    const result = await execute(() => filesAPI.listDepartmentFiles(limit, offset));
    return result;
  }, [execute]);

  // Get department usage
  const getDepartmentUsage = useCallback(async () => {
    const result = await execute(() => filesAPI.getDepartmentUsage());
    return result;
  }, [execute]);

  // Update file metadata
  const moveFile = useCallback(async (fileId, newFolderId) => {
    const result = await execute(() => filesAPI.moveFile(fileId, newFolderId));
    return result;
  }, [execute]);

  const updateFileMetadata = useCallback(async (fileId, data) => {
    const result = await execute(() => filesAPI.updateFileMetadata(fileId, data));
    return result;
  }, [execute]);

  // Delete file
  const deleteFile = useCallback(async (fileId, forcePhysicalDelete = false) => {
    const result = await execute(() => filesAPI.deleteFile(fileId, forcePhysicalDelete));
    return result;
  }, [execute]);

  // Set profile picture
  const setProfilePicture = useCallback(async (staffMemberId, fileId) => {
    const result = await execute(() => filesAPI.setProfilePicture(staffMemberId, fileId));
    return result;
  }, [execute]);

  // Remove profile picture
  const removeProfilePicture = useCallback(async (staffMemberId) => {
    const result = await execute(() => filesAPI.removeProfilePicture(staffMemberId));
    return result;
  }, [execute]);

  // Mark file as read by current user (versioned endpoint if versionNumber provided)
  const markFileRead = useCallback(async (fileId, versionNumber = null) => {
    await execute(() => filesAPI.markFileRead(fileId, versionNumber));
  }, [execute]);

  // Get file readers
  const getFileReaders = useCallback(async (fileId, limit = null, offset = null) => {
    const result = await execute(() => filesAPI.getFileReaders(fileId, limit, offset));
    return result;
  }, [execute]);

  // List all versions of a file
  const listFileVersions = useCallback(async (fileId) => {
    const result = await execute(() => filesAPI.listFileVersions(fileId));
    return result;
  }, [execute]);

  // Upload a new version of an existing file
  const uploadFileVersion = useCallback(async (fileId, file, changeNote = null, options = {}) => {
    if (file.size > MAX_UPLOAD_BYTES) return tooLarge();
    const result = await execute(() => filesAPI.uploadFileVersion(fileId, file, changeNote, {
      onUploadProgress: options.onUploadProgress,
    }));
    return result;
  }, [execute]);

  // Get readers for a specific version
  const getVersionReaders = useCallback(async (fileId, versionNumber, limit = null, offset = null) => {
    const result = await execute(() => filesAPI.getVersionReaders(fileId, versionNumber, limit, offset));
    return result;
  }, [execute]);

  // Get my read history
  const getMyReadHistory = useCallback(async (limit = null, offset = null) => {
    const result = await execute(() => filesAPI.getMyReadHistory(limit, offset));
    return result;
  }, [execute]);

  return {
    loading,
    error,
    clearError,
    // File operations
    uploadFile,
    downloadFile,
    getFileDetails,
    listMyFiles,
    listDepartmentFiles,
    getDepartmentUsage,
    moveFile,
    updateFileMetadata,
    deleteFile,
    // Profile picture operations
    setProfilePicture,
    removeProfilePicture,
    // Read tracking
    markFileRead,
    getFileReaders,
    getMyReadHistory,
    // Version operations
    listFileVersions,
    uploadFileVersion,
    getVersionReaders,
  };
}